/**
 * REPORT FRESHNESS LABELS (RM-W3-12).
 *
 * A stock report is a snapshot: its price and its financials are what the data layer returned when
 * the report was generated, and the newest completed reports are months old. The viewer used to
 * print "Latest" when `dataAsOf` was empty (it always was, `dbRecordToReport` defaulted it to "") and
 * labelled the stored price "(current)", so a May price read as today's. This module is the one
 * place those labels come from, shared by the viewer and the PDF route:
 *
 *   dataAsOf   the persisted snapshot's `content.dataSnapshot.fetchedAt` when it parses, else the
 *              report's `generatedAt` (`createdAt`). Always an ISO instant.
 *   label      "Data as of Sep 23, 2026". Never "Latest".
 *   price      "price at generation". Never "(current)" or "Current Price".
 *   outdated   more than REPORT_OUTDATED_DAYS since dataAsOf, the same 30-day rule
 *              `api/reports/check` already applies for its outdated notice.
 *
 * Pure: no clock of its own. Dates are formatted in UTC so the server and the browser print the same
 * day for the same instant.
 */

/** Matches `isOutdated: ageDays > 30` in src/app/api/reports/check/route.ts. */
export const REPORT_OUTDATED_DAYS = 30;

/** The only wording a stored price may carry. */
export const PRICE_AT_GENERATION_LABEL = "price at generation";

const MS_PER_DAY = 86_400_000;

function parseInstant(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" || value.trim() === "") return null;
  // Only full ISO dates: a fiscal-period label such as "Q3 FY26" must not parse as a date.
  if (!/^\d{4}-\d{2}-\d{2}/.test(value.trim())) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * The instant the report's data describes: the persisted snapshot's `fetchedAt` when present and
 * valid, else `generatedAt`. Returned as an ISO string (the `Report.dataAsOf` field).
 */
export function deriveReportDataAsOf(content: unknown, generatedAt: Date): string {
  const snapshot =
    content !== null && typeof content === "object"
      ? (content as { dataSnapshot?: { fetchedAt?: unknown } }).dataSnapshot
      : undefined;
  const fetchedAt = parseInstant(snapshot?.fetchedAt);
  return (fetchedAt ?? generatedAt).toISOString();
}

export interface ReportFreshness {
  /** The instant the labels describe. */
  asOf: Date;
  /** "Sep 23, 2026". */
  dateLabel: string;
  /** "Data as of Sep 23, 2026 (UTC)". */
  dataAsOfLabel: string;
  /**
   * The generation date in the SAME zone as `dataAsOfLabel`, "Sep 23, 2026 (UTC)". The viewer and the
   * PDF print their "Generated" label from this, so the two adjacent dates can never disagree by a
   * day because one was formatted in the reader's zone and the other in UTC (review P3-6).
   */
  generatedLabel: string;
  /** Whole days from `asOf` to `now`, never negative. */
  ageDays: number;
  isOutdated: boolean;
  /** Plain-English tooltip for the "Data as of" label. */
  tooltip: string;
}

export function formatReportDate(d: Date): string {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/**
 * Labels for a report. `dataAsOf` may be a legacy or empty value (an older cached Report carried a
 * fiscal-period string or ""); anything that is not an ISO instant falls back to `generatedAt`.
 */
export function reportFreshness(dataAsOf: unknown, generatedAt: Date | string, now: Date): ReportFreshness {
  const asOf = parseInstant(dataAsOf) ?? parseInstant(generatedAt) ?? new Date(generatedAt);
  const ageDays = Math.max(0, Math.floor((now.getTime() - asOf.getTime()) / MS_PER_DAY));
  const dateLabel = formatReportDate(asOf);
  const generated = parseInstant(generatedAt) ?? asOf;
  return {
    asOf,
    dateLabel,
    dataAsOfLabel: `Data as of ${dateLabel} (UTC)`,
    generatedLabel: `${formatReportDate(generated)} (UTC)`,
    ageDays,
    isOutdated: ageDays > REPORT_OUTDATED_DAYS,
    tooltip:
      `The price, financials and analysis in this report are a snapshot taken on ${dateLabel}. ` +
      `They are not updated live, so the stock's price today may differ. ` +
      `Reports more than ${REPORT_OUTDATED_DAYS} days old are marked outdated.`,
  };
}
